// src/utils/progressionExperience.js
//
// Pass 78 — the pure view-model for the My Skills / progression EXPERIENCE (/me/progress).
//
// It projects the Pass-77 canonical progression summary (buildStudentProgressSummary → progressionEvidence) into a
// simple, TRUTHFUL, student-facing hierarchy: WHAT AM I WORKING ON (focus) · WHAT HAVE I DEMONSTRATED (level) ·
// WHAT'S NEXT (next step) · WHY (evidence). It never invents progress:
//   · "Current focus" (what coaching evidence says to work on) is DISTINCT from "Demonstrated level" (only a
//     completed lesson whose outcome demonstrated a skill — Pass 77 levelBasis 'lesson_demonstrated_skill').
//   · zero canonical evidence → an honest empty state (no beginner / no fabricated skill / track).
//   · evidence links back to the canonical source (review → /reviews/:id, lesson → /lessons/:id); internal ids /
//     provenance keys / weights / confidence numbers are NEVER surfaced.
// Pure: no IO, no Firebase, no Date.now. Deterministic given a summary.

import { PROGRESSION_LEVELS } from './progressionConstants';
import { buildStudentLessonPath } from './studentLessonPath';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Deterministic short date from an epoch-seconds value (UTC, so it is stable across environments/tests).
function formatShortDate(seconds) {
  if (!seconds || typeof seconds !== 'number') return '';
  const d = new Date(seconds * 1000);
  const day = d.getUTCDate();
  const month = MONTHS[d.getUTCMonth()];
  if (!month || !day) return '';
  return `${day} ${month}`;
}

function levelLabel(level) {
  if (!level) return '';
  if (PROGRESSION_LEVELS.indexOf(level) === -1) return '';
  return String(level)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Translate a canonical source into a plain-language KIND (never expose provenance/signalType keys to the student).
function sourceKind(signal) {
  if (signal?.sourceType === 'review') return 'Coach review';
  if (signal?.signalType === 'lesson_next_skill_hint') return 'Recommended next';
  return 'Completed lesson';
}

// Strip the "review:"/"lesson:" prefix that keys the canonical source; never let it leak into a visible URL/text.
function rawSourceId(sourceId = '') {
  return String(sourceId || '').replace(/^(?:review|lesson):/, '');
}

// A source is routable only when it resolves to a real, linkable id (not a synthetic fallback like idx0 / unknowns).
function sourceRoute(signal) {
  const id = rawSourceId(signal?.sourceId);
  if (!id || /^idx\d+$/.test(id)) return '';
  if (signal?.sourceType === 'review') return `/reviews/${id}`;
  if (signal?.sourceType === 'lesson') return buildStudentLessonPath({ lessonId: id });
  return '';
}

// Build the concise evidence list behind the current focus (deduped by canonical source, newest-ish first as ranked).
export function buildEvidenceItems(summary = {}) {
  const signals = summary?.progressionInput?.sourceSignals || summary?.sourceSignals || [];
  const seen = new Set();
  const items = [];
  signals.forEach((signal) => {
    const key = String(signal?.sourceId || '');
    if (!key || seen.has(key)) return;
    seen.add(key);
    const route = sourceRoute(signal);
    items.push({
      key,
      kind: sourceKind(signal),
      label: signal?.label || signal?.skillFocus || '',
      dateText: formatShortDate(signal?.occurredAt),
      route,
      hasRoute: Boolean(route),
    });
  });
  return items;
}

export function buildProgressionExperience(summary = {}) {
  const hasEvidence =
    summary?.hasProgressionEvidence === true || summary?.progressionInput?.hasEvidence === true;

  const focusName =
    summary?.currentSkill?.name ||
    summary?.progressionRecommendation?.skillFocus ||
    summary?.progressionInput?.skillFocus ||
    '';
  const nextName = summary?.nextSkill?.name || '';

  // Demonstrated level is DEMONSTRATED-lesson-only (Pass 77). A review focus never establishes a level.
  const levelEstablished =
    summary?.levelBasis === 'lesson_demonstrated_skill' && Boolean(levelLabel(summary?.currentLevel));
  const demonstratedLevel = levelEstablished ? levelLabel(summary.currentLevel) : '';

  const reason = hasEvidence
    ? (summary?.recommendationReason || summary?.progressionInput?.recommendationReason || '')
    : '';

  const evidence = hasEvidence ? buildEvidenceItems(summary) : [];

  return {
    hasEvidence,
    sport: summary?.dominantSport || summary?.sport || '',
    focus: {
      hasFocus: hasEvidence && Boolean(focusName),
      name: hasEvidence ? focusName : '',
      value: hasEvidence && focusName ? focusName : 'Not established yet',
    },
    demonstratedLevel: {
      established: levelEstablished,
      value: levelEstablished ? demonstratedLevel : 'Not established yet',
    },
    nextStep: {
      established: hasEvidence && Boolean(nextName),
      name: hasEvidence ? nextName : '',
      value: hasEvidence && nextName ? nextName : 'No next step established yet',
    },
    why: {
      hasReason: Boolean(reason),
      text: reason,
      evidence,
    },
    emptyState: {
      title: "We don't have enough coaching evidence to establish your progression yet.",
      body:
        'Upload a coaching clip or complete a lesson and your focus, level, and next step will appear here — always tied to real coach review or lesson evidence.',
    },
  };
}
